import type { IndustryPack } from '../types'

export const HEALTHCARE_V1: IndustryPack = {
  pack_id: 'healthcare_v1',
  industry: 'healthcare',
  pack_name: 'Healthcare Comprehension & Compliance',
  version: 1,
  rules: [
    {
      rule_id: 'HC_001_readability',
      severity: 'warn',
      match: { type: 'readability', metric: 'flesch_kincaid', threshold: 8, operator: '>=' },
      message: 'Reading grade level above 8th grade. Health literacy guidelines recommend ≤ 6th–8th grade for patient communications.',
      cited_authority: 'CDC Health Literacy Guidelines; AMA Health Literacy Project',
      suggested_rewrite_template: 'Shorten sentences. Replace medical terms with plain English. Use active voice.',
    },
    {
      rule_id: 'HC_002_medical_jargon',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['myocardial', 'prophylactic', 'contraindicated', 'etiology', 'pathophysiology', 'prognosis', 'comorbidities', 'subcutaneous', 'intravenous', 'adjuvant'] },
      message: 'Medical jargon density. Patient literacy research shows these terms reduce comprehension by 40–60%.',
      cited_authority: 'AHRQ Health Literacy Universal Precautions Toolkit (2nd ed.)',
      suggested_rewrite_template: 'Replace jargon with plain language equivalents, e.g. "prophylactic" → "preventive".',
    },
    {
      rule_id: 'HC_003_not_medical_advice',
      severity: 'info',
      match: { type: 'keyword_list', keywords: ['consult your doctor', 'speak to your physician', 'ask your healthcare provider'] },
      message: 'Medical advice disclaimer present — good. Confirm "not medical advice" also appears at the end of this communication.',
    },
    {
      rule_id: 'HC_004_hipaa_pii',
      severity: 'block',
      match: { type: 'regex', pattern: '\\b\\d{3}-\\d{2}-\\d{4}\\b|\\bSSN\\b|\\bsocial security\\b', flags: 'i' },
      message: 'Potential SSN or identifier in stimulus. Never include PII in research materials.',
      cited_authority: 'HIPAA Privacy Rule (45 C.F.R. §§ 164.502–164.514)',
    },
    {
      rule_id: 'HC_005_medicare_disclosure',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['medicare', 'medicaid', 'cms', 'cms-regulated', 'star rating', 'plan star'] },
      message: 'Medicare/Medicaid reference. CMS marketing guidelines require specific disclosures and prohibit misleading language.',
      cited_authority: 'CMS Medicare Communications and Marketing Guidelines (MCMG)',
    },
    {
      rule_id: 'HC_006_disenrollment_threats',
      severity: 'block',
      match: { type: 'keyword_list', keywords: ['you will be disenrolled', 'coverage will be terminated', 'lose your benefits', 'forfeited'] },
      message: 'Disenrollment threat language. CMS guidelines prohibit language that creates fear of losing coverage without proper context.',
      cited_authority: 'CMS MCMG §100.1 (Prohibited Practices)',
    },
    {
      rule_id: 'HC_007_benefit_comprehension',
      severity: 'info',
      match: { type: 'keyword_list', keywords: ['copay', 'copayment', 'coinsurance', 'deductible', 'out-of-pocket maximum'] },
      message: 'Cost-sharing terms present. Research shows 60% of members misunderstand these terms. Consider adding a plain-language gloss.',
      cited_authority: 'NCQA Health Plan Accreditation Standards; CMS SBC requirements',
    },
    {
      rule_id: 'HC_008_pediatric_appropriateness',
      severity: 'info',
      match: { type: 'keyword_list', keywords: ['children', 'pediatric', 'kids', 'minors', 'under 18'] },
      message: 'Pediatric audience reference. Ensure parental consent language is present and readability targets 4th–6th grade.',
    },
    {
      rule_id: 'HC_009_emergency_clarity',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['emergency', '911', 'urgent care', 'ER', 'emergency room'] },
      message: 'Emergency reference. Ensure the message clearly distinguishes between 911 (life-threatening) and urgent care use.',
      cited_authority: 'ACEP Emergency Department Guidelines for Health Plan Communications',
    },
    {
      rule_id: 'HC_010_star_ratings_claim',
      severity: 'warn',
      match: { type: 'regex', pattern: '(\\d(\\.\\d)?\\s*star|rated\\s+\\d)', flags: 'i' },
      message: 'Star rating claim. CMS requires exact star rating values and disclaimers in all member communications.',
      cited_authority: 'CMS MCMG §100 (Star Ratings Communication Rules)',
    },
  ],
}
